import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/config/axiosInstance";
import { getCMSApiRoute } from "@/helpers/api.util";
import Cookies from "js-cookie";

/**
 * Showcase Redux Slice
 *
 * Manages home showcase data (continue-watch, you-may-like, recently-updated)
 * in Redux for reactive updates across pages.
 *
 * When the player exits, it dispatches fetchContinueWatch() to refresh data,
 * and home.jsx re-renders automatically via useSelector.
 */

// ============================================================================
// CACHE
// ============================================================================

const CACHE_VERSION = "1.0";
const CACHE_PREFIX = "home_showcase_";

const getCachedData = (key) => {
    if (typeof window === "undefined") return null;
    try {
        const cached = localStorage.getItem(CACHE_PREFIX + key);
        if (!cached) return null;
        const parsed = JSON.parse(cached);
        if (parsed.version !== CACHE_VERSION) {
            localStorage.removeItem(CACHE_PREFIX + key);
            return null;
        }
        return parsed.data;
    } catch {
        return null;
    }
};

const setCachedData = (key, data) => {
    if (typeof window === "undefined") return;
    try {
        const cacheEntry = { data, timestamp: Date.now(), version: CACHE_VERSION };
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheEntry));
    } catch { }
};

// ============================================================================
// TRANSFORM
// ============================================================================

const transformContinueWatch = (items) => {
    if (!Array.isArray(items)) return [];

    return items.map((item) => {
        const content = item.content || {};
        const options = item.options || {};
        const watchedEpisode = content.watchedEpisode || item.watchedEpisode || {};

        const rawSeason =
            item.seasonNumber ?? item.season ??
            content.seasonNumber ?? content.season ??
            options.seasonNumber ?? options.season ??
            watchedEpisode.seasonNumber ?? watchedEpisode.season;

        const rawEpisode =
            item.episodeNumber ?? item.episode ??
            content.episodeNumber ?? content.episode ??
            options.episodeNumber ?? options.episode ??
            watchedEpisode.episodeNumber ?? watchedEpisode.episode;

        const seasonNumber = typeof rawSeason === "number" ? rawSeason
            : typeof rawSeason === "string" ? parseInt(rawSeason, 10) : null;
        const episodeNumber = typeof rawEpisode === "number" ? rawEpisode
            : typeof rawEpisode === "string" ? parseInt(rawEpisode, 10) : null;

        return {
            ...content,
            elapsedTime: item.elapsedTime || 0,
            totalTime: item.totalTime || 0,
            updatedAt: item.updatedAt,
            seasonNumber: isNaN(seasonNumber) ? null : seasonNumber,
            episodeNumber: isNaN(episodeNumber) ? null : episodeNumber,
        };
    });
};

// ============================================================================
// ASYNC THUNK
// ============================================================================

/**
 * Fetch continue-watch data from API, transform, and update both Redux & cache.
 * Dispatch this when: player exits, user navigates home, etc.
 */
export const fetchContinueWatch = createAsyncThunk(
    "showcase/fetchContinueWatch",
    async (_, { rejectWithValue }) => {
        try {
            const token = Cookies.get("userAccessToken");
            if (!token || token.length === 0) return [];

            const response = await axiosInstance.get(
                getCMSApiRoute("showcase/continue-watch")
            );

            let results = [];
            if (response?.data?.data?.results) {
                results = response.data.data.results;
            } else if (response?.data?.results) {
                results = response.data.results;
            }

            if (results.length > 0) {
                results = transformContinueWatch(results);
            }

            // Sync to localStorage cache
            setCachedData("continueWatch", results);
            console.log("[Showcase] Continue-watch refreshed:", results.length, "items");
            return results;
        } catch (error) {
            console.warn("[Showcase] fetchContinueWatch error:", error);
            return rejectWithValue(error.message);
        }
    }
);

// ============================================================================
// SLICE
// ============================================================================

const initialState = {
    continueWatch: [],
    youMayLike: [],
    recentlyUpdated: [],
    loading: false,
};

const showcaseSlice = createSlice({
    name: "showcase",
    initialState,
    reducers: {
        setContinueWatch: (state, action) => {
            state.continueWatch = action.payload || [];
        },
        setYouMayLike: (state, action) => {
            state.youMayLike = action.payload || [];
        },
        setRecentlyUpdated: (state, action) => {
            state.recentlyUpdated = action.payload || [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchContinueWatch.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchContinueWatch.fulfilled, (state, action) => {
                state.continueWatch = action.payload || [];
                state.loading = false;
            })
            .addCase(fetchContinueWatch.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const { setContinueWatch, setYouMayLike, setRecentlyUpdated } =
    showcaseSlice.actions;

export default showcaseSlice.reducer;
